import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Grid, Button, CircularProgress, Chip, Dialog, 
    DialogTitle, DialogContent, DialogActions, DialogContentText, TextField,
    Alert, Snackbar, Divider, Tabs, Tab, Paper
} from '@mui/material';
import {
    DollarSign, Calendar, RefreshCw, AlertCircle, Clock, CheckCircle, XCircle,
    Info, CreditCard, ShieldCheck, Tag, ArrowRight, RefreshCcw, PhoneForwarded
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

// Glass Card Component for consistent styling
const GlassCard = ({ children, darkMode }) => {
    return (
        <Box sx={{
            borderRadius: '24px',
            overflow: 'hidden',
            background: darkMode ? 'rgba(32, 33, 44, 0.35)' : 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(16px)',
            border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'}`,
            boxShadow: darkMode ? '0 10px 30px rgba(0, 0, 0, 0.25)' : '0 10px 30px rgba(0, 0, 0, 0.1)'
        }}>
            {children}
        </Box>
    );
};

const Subscriptions = ({ darkMode, service, getAuthToken }) => {
    const navigate = useNavigate();
    
    // State management
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [subscriptions, setSubscriptions] = useState([]);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
    
    // Enhanced state management for dialogs
    const [dialogType, setDialogType] = useState('cancel'); // 'cancel' or 'switch'
    const [dialogTab, setDialogTab] = useState(0); // 0 = options, 1 = confirm cancel, 2 = success
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [selectedSubscription, setSelectedSubscription] = useState(null);
    const [cancelReason, setCancelReason] = useState('');
    const [cancelInProgress, setCancelInProgress] = useState(false);
    const [cancelSuccessMessage, setCancelSuccessMessage] = useState('');

    // Fetch subscriptions on component mount
    useEffect(() => {
        fetchSubscriptions();
    }, []);

    const fetchSubscriptions = async () => {
        try {
            setLoading(true);
            
            // Get auth token
            const token = await getAuthToken();
            
            // Use payment service to fetch subscriptions
            const response = await service.getSubscriptions(token);
            
            // Sort subscriptions by status (active first, then canceling, then others)
            const sortedSubscriptions = [...response.data].sort((a, b) => {
                const statusOrder = { 'active': 0, 'trialing': 1, 'canceling': 2 };
                const aOrder = statusOrder[a.status] !== undefined ? statusOrder[a.status] : 99;
                const bOrder = statusOrder[b.status] !== undefined ? statusOrder[b.status] : 99;
                return aOrder - bOrder;
            });
            
            setSubscriptions(sortedSubscriptions);
            
            // Show notification if any subscriptions are in canceling state
            const cancelingCount = response.data.filter(sub => sub.status === 'canceling').length;
            if (cancelingCount > 0) {
                setNotification({
                    open: true,
                    message: `You have ${cancelingCount} subscription${cancelingCount > 1 ? 's' : ''} scheduled to cancel at the end of the billing period.`,
                    severity: 'info'
                });
            }
        } catch (err) {
            console.error('Error fetching subscriptions:', err);
            setError(err.friendlyMessage || 'Failed to fetch subscriptions. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Open cancel/switch dialog with options
    const openManageDialog = (subscription) => {
        setSelectedSubscription(subscription);
        setCancelDialogOpen(true);
        setCancelReason('');
        setCancelSuccessMessage('');
        setDialogTab(0); // Start with options tab
    };

    // Close cancel dialog
    const closeDialog = () => {
        setCancelDialogOpen(false);
        setSelectedSubscription(null);
        setCancelReason('');
        setCancelSuccessMessage('');
        setDialogTab(0);
        
        // Refresh subscriptions list after successful cancellation
        if (cancelSuccessMessage) {
            fetchSubscriptions();
        }
    };

    // Handle tab change in dialog
    const handleTabChange = (event, newValue) => {
        setDialogTab(newValue);
    };

    // Switch carrier flow
    const handleSwitchCarrier = () => {
        if (!selectedSubscription) return;
        
        // Navigate to the transfer switching flow with the subscription information
        navigate('/transfer-carrier', { 
          state: { 
            existingSubscription: selectedSubscription,
            isTransfer: true
          }
        });
        
        // Close dialog after navigating
        closeDialog();
      };

    // Enhanced cancel subscription handler
    const handleCancelSubscription = async () => {
        if (!selectedSubscription) return;
        
        setCancelInProgress(true);

        try {
            // Get auth token
            const token = await getAuthToken();
            
            // Send cancellation request to backend using payment service
            const response = await service.cancelSubscription({
                subscription_id: selectedSubscription.id,
                reason: cancelReason || 'User requested cancellation'
            }, token);

            console.log('Cancellation response:', response);

            // Update the UI based on response
            const updatedSubscriptions = subscriptions.map(sub => {
                if (sub.id === selectedSubscription.id) {
                    return { 
                        ...sub, 
                        status: response.data.order_status || 'canceling',
                        cancel_at: response.data.cancel_at
                    };
                }
                return sub;
            });

            setSubscriptions(updatedSubscriptions);
            
            // Show success message from the server if available
            if (response.data.message) {
                setCancelSuccessMessage(response.data.message);
            } else {
                // Default success message with formatted end date if available
                const endDate = response.data.cancel_at 
                    ? formatDate(new Date(response.data.cancel_at * 1000)) 
                    : 'the end of your billing period';
                
                setCancelSuccessMessage(
                    `Your subscription will remain active until ${endDate}, at which point it will be canceled.`
                );
            }
            
            // Show success state
            setDialogTab(2);
        } catch (err) {
            console.error('Error cancelling subscription:', err);
            
            // Extract error message if available in response
            const errorMessage = err.friendlyMessage || 'Failed to cancel subscription. Please try again.';
            
            setError(errorMessage);
            setNotification({
                open: true,
                message: errorMessage,
                severity: 'error'
            });
            
            closeDialog();
        } finally {
            setCancelInProgress(false);
        }
    };

    // Proceed to cancellation confirmation
    const proceedToCancelConfirmation = () => {
        setDialogType('cancel');
        setDialogTab(1);
    };

    // Function to format date to readable format
    const formatDate = (dateString) => {
        if (!dateString) return '';
        
        // Handle both string dates and timestamp numbers
        const date = typeof dateString === 'number' 
            ? new Date(dateString * 1000) 
            : new Date(dateString);
            
        // Check if date is valid
        if (isNaN(date.getTime())) return '';
        
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        return date.toLocaleDateString('en-US', options);
    };

    // Function to get subscription display name
    const getSubscriptionDisplayName = (subscription) => {
        // If we have display_name from backend, use it
        if (subscription.display_name) {
            return subscription.display_name;
        }

        // If plan_name already includes the circle name, use it directly
        if (subscription.plan_name &&
            subscription.circle_name &&
            subscription.plan_name.startsWith(`${subscription.circle_name} - `)) {
            return subscription.plan_name;
        }

        // Legacy fallback logic - only combine if needed
        if (subscription.circle_name && subscription.plan_name) {
            // Check if plan_name already has some formatting
            if (subscription.plan_name.includes(" - ")) {
                // It already has some formatting, return as is
                return subscription.plan_name;
            }
            return `${subscription.circle_name} - ${subscription.plan_name}`;
        }

        if (subscription.circle_name) {
            return subscription.circle_name;
        }

        return subscription.plan_name || 'Subscription';
    };

    // Function to format currency
    const formatCurrency = (amount) => {
        if (!amount) return '$0.00';
        
        const numAmount = typeof amount === 'number' 
            ? amount 
            : parseFloat(amount);
            
        return `$${numAmount.toFixed(2)}`;
    };

    // Function to get price display with correct period
    const getPriceDisplay = (subscription) => {
        // Get subscription type
        const subscriptionType = subscription.subscription_type;
        
        // Return fixed subscription cost based on subscription type
        if (subscriptionType === 'annual') {
            return '$96.00 / year';
        } else if (subscriptionType === '3months') {
            return '$36.00 / 3 months';
        }
        
        // Fallback for unknown subscription types (should rarely happen)
        const formattedAmount = formatCurrency(subscription.amount);
        return `${formattedAmount} / month`;
    };

    // Render status component with color and icon
    const renderStatusChip = (status) => {
        let color = 'primary';
        let label = status.charAt(0).toUpperCase() + status.slice(1);
        let icon = null;
        
        switch(status) {
            case 'active':
                color = '#4CAF50';
                icon = <CheckCircle size={14} />;
                break;
            case 'canceling':
                color = '#FF9800';
                label = 'Canceling';
                icon = <Clock size={14} />;
                break;
            case 'cancelled':
                color = '#9E9E9E';
                label = 'Canceled';
                icon = <XCircle size={14} />;
                break;
            case 'trialing':
                color = '#2196F3';
                label = 'Trial';
                icon = <Info size={14} />;
                break;
            case 'payment_failed':
                color = '#F44336';
                label = 'Payment Failed';
                icon = <AlertCircle size={14} />;
                break;
            case 'past_due':
                color = '#F44336';
                label = 'Past Due';
                icon = <CreditCard size={14} />;
                break;
            case 'incomplete':
                color = '#FF9800';
                label = 'Incomplete';
                icon = <AlertCircle size={14} />;
                break;
            default:
                icon = <AlertCircle size={14} />;
        }
        
        return (
            <Chip
                icon={icon ? <Box component="span" sx={{ ml: 0.5, mr: -0.5, display: 'flex' }}>{icon}</Box> : undefined}
                label={label}
                size="small"
                sx={{
                    backgroundColor: `${color}15`,
                    color: color,
                    fontWeight: 600,
                    '& .MuiChip-label': {
                        display: 'flex',
                        alignItems: 'center',
                    }
                }}
            />
        );
    };

    // Render subscription status chip
    const renderSubscriptionStatusChip = (subscriptionStatus) => {
        if (!subscriptionStatus) return null;
        
        let color = '#5C6BC0'; // Default indigo color
        let label = formatSubscriptionStatus(subscriptionStatus);
        let icon = <Tag size={14} />;
        
        // You can customize colors for different subscription statuses
        switch(subscriptionStatus) {
            case 'active':
            case 'activate':
                color = '#4CAF50'; // Green
                break;
            case 'pending_approval':
                color = '#FF9800'; // Orange
                break;
            case 'approve':
                color = '#2196F3'; // Blue
                break;
            case 'decline':
                color = '#F44336'; // Red
                break;
            default:
                // Keep default color
        }
        
        return (
            <Chip
                icon={<Box component="span" sx={{ ml: 0.5, mr: -0.5, display: 'flex' }}>{icon}</Box>}
                label={label}
                size="small"
                variant="outlined"
                sx={{
                    borderColor: color,
                    color: color,
                    fontWeight: 500,
                    '& .MuiChip-label': {
                        display: 'flex',
                        alignItems: 'center',
                    }
                }}
            />
        );
    };

    // Format subscription status from snake_case to Title Case
    const formatSubscriptionStatus = (status) => {
        if (!status) return 'Unknown';
        
        // Handle special cases
        if (status === 'activate') return 'Active';
        
        // Format snake_case to Title Case
        return status.split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    // Get next billing or end date display based on subscription status
    const getNextBillingDisplay = (subscription) => {
        if (subscription.status === 'canceling' || subscription.cancel_at_period_end) {
            const endDate = subscription.cancel_at 
                ? formatDate(new Date(subscription.cancel_at * 1000))
                : formatDate(subscription.monthly_payment_date);
                
            return `Ends ${endDate}`;
        }
        
        return formatDate(subscription.monthly_payment_date || subscription.next_billing_date);
    };

    // Render the options dialog content
    const renderOptionsContent = () => {
        return (
            <>
                <DialogTitle sx={{ textAlign: 'center', pt: 3 }}>
                    Manage Your Subscription
                </DialogTitle>
                <DialogContent>
                    <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mb: 3, textAlign: 'center' }}>
                        What would you like to do with your subscription?
                    </Typography>
                    
                    <Grid container spacing={3}>
                        {/* Switch Carrier Option */}
                        <Grid item xs={12} md={6}>
                            <Paper
                                elevation={3}
                                sx={{
                                    p: 3,
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    bgcolor: 'rgba(33, 150, 243, 0.05)',
                                    border: '1px solid rgba(33, 150, 243, 0.2)',
                                    '&:hover': {
                                        bgcolor: 'rgba(33, 150, 243, 0.1)',
                                        transform: 'translateY(-4px)',
                                        transition: 'transform 0.3s'
                                    }
                                }}
                                onClick={handleSwitchCarrier}
                            >
                                <Box
                                    sx={{
                                        width: 60,
                                        height: 60,
                                        borderRadius: '50%',
                                        bgcolor: 'rgba(33, 150, 243, 0.1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mb: 2
                                    }}
                                >
                                    <PhoneForwarded size={30} color="#2196F3" />
                                </Box>
                                <Typography variant="h6" fontWeight={600} gutterBottom>
                                    Switch Carrier
                                </Typography>
                                <Typography align="center" color="textSecondary">
                                    Keep your CircledIn subscription and switch to a different carrier.
                                </Typography>
                                <Box sx={{ flexGrow: 1 }} />
                                <Box
                                    sx={{ 
                                        mt: 3, 
                                        p: 1, 
                                        bgcolor: 'rgba(33, 150, 243, 0.1)',
                                        borderRadius: 1,
                                        width: '100%'
                                    }}
                                >
                                    <Typography variant="body2" align="center">
                                        <strong>Save money!</strong> No additional CircledIn subscription fee
                                    </Typography>
                                </Box>
                            </Paper>
                        </Grid>
                        
                        {/* Cancel Subscription Option */}
                        <Grid item xs={12} md={6}>
                            <Paper
                                elevation={3}
                                sx={{
                                    p: 3,
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    bgcolor: 'rgba(244, 67, 54, 0.05)',
                                    border: '1px solid rgba(244, 67, 54, 0.2)',
                                    '&:hover': {
                                        bgcolor: 'rgba(244, 67, 54, 0.1)',
                                        transform: 'translateY(-4px)',
                                        transition: 'transform 0.3s'
                                    }
                                }}
                                onClick={proceedToCancelConfirmation}
                            >
                                <Box
                                    sx={{
                                        width: 60,
                                        height: 60,
                                        borderRadius: '50%',
                                        bgcolor: 'rgba(244, 67, 54, 0.1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mb: 2
                                    }}
                                >
                                    <XCircle size={30} color="#F44336" />
                                </Box>
                                <Typography variant="h6" fontWeight={600} gutterBottom>
                                    Cancel Subscription
                                </Typography>
                                <Typography align="center" color="textSecondary">
                                    Cancel your subscription and lose access at the end of your billing period.
                                </Typography>
                                <Box sx={{ flexGrow: 1 }} />
                                <Box
                                    sx={{ 
                                        mt: 3, 
                                        p: 1, 
                                        bgcolor: 'rgba(244, 67, 54, 0.1)',
                                        borderRadius: 1,
                                        width: '100%'
                                    }}
                                >
                                    <Typography variant="body2" align="center">
                                        Your service will end on {getNextBillingDisplay(selectedSubscription || {})}
                                    </Typography>
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', p: 3 }}>
                    <Button 
                        onClick={closeDialog}
                        variant="outlined"
                        sx={{
                            borderRadius: '12px',
                            textTransform: 'none',
                            minWidth: 100
                        }}
                    >
                        Keep My Plan
                    </Button>
                </DialogActions>
            </>
        );
    };

    // Render the cancellation confirmation dialog content
    const renderCancellationContent = () => {
        return (
            <>
                <DialogTitle sx={{ pt: 3 }}>
                    Cancel Subscription
                </DialogTitle>
                <DialogContent>
                    <DialogContentText mb={3}>
                        Are you sure you want to cancel your subscription to <strong>{selectedSubscription ? getSubscriptionDisplayName(selectedSubscription) : ''}</strong>? 
                        Your subscription will remain active until the end of your current billing period.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="cancel-reason"
                        label="Reason for cancellation (optional)"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button 
                        onClick={() => setDialogTab(0)} // Go back to options
                        disabled={cancelInProgress}
                        sx={{
                            borderRadius: '12px',
                            textTransform: 'none'
                        }}
                    >
                        Back
                    </Button>
                    <Button 
                        onClick={handleCancelSubscription}
                        disabled={cancelInProgress}
                        variant="contained"
                        color="error"
                        sx={{
                            borderRadius: '12px',
                            textTransform: 'none',
                            minWidth: 120
                        }}
                    >
                        {cancelInProgress ? <CircularProgress size={20} color="inherit" /> : 'Yes, Cancel'}
                    </Button>
                </DialogActions>
            </>
        );
    };

    // Render the success dialog content
    const renderSuccessContent = () => {
        return (
            <>
                <DialogTitle sx={{ textAlign: 'center', pt: 3 }}>
                    <CheckCircle color="#4CAF50" size={40} style={{ margin: '0 auto 16px' }} />
                    Subscription Canceled
                </DialogTitle>
                <DialogContent>
                    <DialogContentText textAlign="center" mb={2}>
                        {cancelSuccessMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                    <Button 
                        onClick={closeDialog}
                        variant="contained"
                        sx={{
                            backgroundColor: '#4CAF50',
                            '&:hover': { backgroundColor: '#3d8b40' },
                            borderRadius: '12px',
                            textTransform: 'none',
                            minWidth: 120
                        }}
                    >
                        Done
                    </Button>
                </DialogActions>
            </>
        );
    };

    return (
        <Box sx={{ mb: 4, position: 'relative' }}>
            {/* Fixed position notification banner at the top of the component */}
            {notification.open && (
                <Box 
                    sx={{ 
                        width: '100%', 
                        position: 'sticky', 
                        top: 0, 
                        zIndex: 1000,
                        mb: 3,
                    }}
                >
                    <Alert 
                        onClose={() => setNotification({...notification, open: false})} 
                        severity={notification.severity}
                        sx={{ 
                            width: '100%', 
                            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                            borderRadius: '8px',
                        }}
                    >
                        {notification.message}
                    </Alert>
                </Box>
            )}

            <Typography variant="h6" fontWeight={600} mb={3}>
                Active Subscriptions
            </Typography>

            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                    <CircularProgress size={40} sx={{ color: '#FF4775' }} />
                </Box>
            ) : error ? (
                <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                    <Typography color="error">{error}</Typography>
                </Box>
            ) : (
                <Grid container spacing={3}>
                    {subscriptions.length > 0 ? (
                        subscriptions.map((subscription, index) => (
                            <Grid item xs={12} md={6} key={subscription.id}>
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                >
                                    <GlassCard darkMode={darkMode}>
                                        <Box p={3}>
                                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                                <Typography variant="h6" fontWeight={700}>
                                                    {getSubscriptionDisplayName(subscription)}
                                                </Typography>
                                                {renderStatusChip(subscription.status)}
                                            </Box>

                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 1.5,
                                                    mb: 3
                                                }}
                                            >
                                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                                    <Typography variant="body2" color="textSecondary">
                                                        <DollarSign size={16} style={{ verticalAlign: 'middle', marginRight: '4px', marginTop: '-2px' }} />
                                                        Circledin Subscription
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {getPriceDisplay(subscription)}
                                                    </Typography>
                                                </Box>

                                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                                    <Typography variant="body2" color="textSecondary">
                                                        <Calendar size={16} style={{ verticalAlign: 'middle', marginRight: '4px', marginTop: '-2px' }} />
                                                        Next billing
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {getNextBillingDisplay(subscription)}
                                                    </Typography>
                                                </Box>

                                                {/* New row for Subscription Status */}
                                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                                    <Typography variant="body2" color="textSecondary">
                                                        <Info size={16} style={{ verticalAlign: 'middle', marginRight: '4px', marginTop: '-2px' }} />
                                                        Carrier Status
                                                    </Typography>
                                                    <Box>
                                                        {renderSubscriptionStatusChip(subscription.subscription_status)}
                                                    </Box>
                                                </Box>

                                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                                    <Typography variant="body2" color="textSecondary">
                                                        <ShieldCheck size={16} style={{ verticalAlign: 'middle', marginRight: '4px', marginTop: '-2px' }} />
                                                        Start Date
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {formatDate(subscription.created_at)}
                                                    </Typography>
                                                </Box>
                                                
                                                {subscription.status === 'canceling' && (
                                                    <Box mt={1} p={1.5} bgcolor={darkMode ? 'rgba(255, 152, 0, 0.1)' : 'rgba(255, 152, 0, 0.05)'} borderRadius={1}>
                                                        <Typography variant="body2" color="#FF9800">
                                                            <Clock size={14} style={{ verticalAlign: 'text-bottom', marginRight: '6px' }} />
                                                            This subscription is scheduled to end at the next billing date. You'll have access until then.
                                                        </Typography>
                                                    </Box>
                                                )}
                                                
                                                {subscription.status === 'payment_failed' && (
                                                    <Box mt={1} p={1.5} bgcolor={darkMode ? 'rgba(244, 67, 54, 0.1)' : 'rgba(244, 67, 54, 0.05)'} borderRadius={1}>
                                                        <Typography variant="body2" color="#F44336">
                                                            <AlertCircle size={14} style={{ verticalAlign: 'text-bottom', marginRight: '6px' }} />
                                                            There was a problem with your last payment. Please update your payment method.
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>

                                            <Box display="flex" justifyContent="space-between" gap={2}>
                                                <Button
                                                    variant="outlined"
                                                    fullWidth
                                                    disabled={subscription.status !== 'active' && subscription.status !== 'trialing'}
                                                    onClick={() => openManageDialog(subscription)}
                                                    sx={{
                                                        borderRadius: '12px',
                                                        textTransform: 'none',
                                                        borderColor: 'rgba(255, 71, 117, 0.5)',
                                                        color: '#FF4775',
                                                        '&:hover': {
                                                            borderColor: '#FF4775',
                                                            backgroundColor: 'rgba(255, 71, 117, 0.08)'
                                                        },
                                                        '&.Mui-disabled': {
                                                            borderColor: 'rgba(0, 0, 0, 0.12)',
                                                            color: 'rgba(0, 0, 0, 0.26)'
                                                        }
                                                    }}
                                                >
                                                    {subscription.status === 'canceling' ? 'View' : 'Manage'}
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    fullWidth
                                                    sx={{
                                                        borderRadius: '12px',
                                                        textTransform: 'none',
                                                        borderColor: darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                                                        color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                                                    }}
                                                >
                                                    View Details
                                                </Button>
                                            </Box>
                                        </Box>
                                    </GlassCard>
                                </motion.div>
                            </Grid>
                        ))
                    ) : (
                        <Grid item xs={12}>
                            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" p={4}>
                                <Box
                                    sx={{
                                        width: 70,
                                        height: 70,
                                        borderRadius: '50%',
                                        background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mb: 2
                                    }}
                                >
                                    <RefreshCw size={30} color={darkMode ? "#A7ABBE" : "#767991"} />
                                </Box>
                                <Typography variant="h6" fontWeight={600} gutterBottom>
                                    No active subscriptions
                                </Typography>
                                <Typography variant="body2" color="textSecondary" textAlign="center">
                                    You don't have any active subscriptions at the moment.
                                </Typography>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            )}

            {/* Enhanced Cancel/Switch Subscription Dialog */}
            <Dialog 
                open={cancelDialogOpen} 
                onClose={closeDialog}
                maxWidth="md"
                fullWidth
            >
                {dialogTab === 0 && renderOptionsContent()}
                {dialogTab === 1 && renderCancellationContent()}
                {dialogTab === 2 && renderSuccessContent()}
            </Dialog>
        </Box>
    );
};

export default Subscriptions;